import {Dialog, MenuItem, Select, TextField} from "@mui/material";
import {FormattedMessage, defineMessage} from "react-intl";
import MultipleLineError from "../../MultipleLineError";
import {SpeedDialInput} from "../../../api";
import ErrorHelper from "../../../utils/ErrorHelper";
import {formatShortNumberForSpeedDial} from "../../../tools";
import {
    DialogActions,
    DialogContent,
    DialogTitle,
    FormattedFormRow,
    FormContainer,
    FormDialogActionButtons,
    LoadingIcon,
} from "@dvrt/graphql-widgets";
import {
    SaveSpeedDialMutation,
    SaveSpeedDialMutationVariables,
    useSaveSpeedDialMutation,
    useSpeedDialShortNumbersQuery,
} from "./queries.generated";
import {Form} from "@/utils/Form";
import {useState} from "react";

class SaveDialogForm extends Form<SaveSpeedDialMutation, SaveSpeedDialMutationVariables, SpeedDialInput> {
    constructor(
        userId: number,
        firstAvailableShortNumber: string,
        speedDialData: Omit<SpeedDialInput, "userId"> | undefined,
    ) {
        super("saveSpeedDial");
        this.hooks(
            useSaveSpeedDialMutation,
            SaveDialogForm.getInitialValues(speedDialData, userId, firstAvailableShortNumber),
        );
    }

    protected override get filteredVariables(): SaveSpeedDialMutationVariables {
        return {
            speedDialInput: this.values,
        };
    }

    private static getInitialValues(
        speedDialData: Omit<SpeedDialInput, "userId"> | undefined,
        userId: number,
        firstAvailableShortNumber: string,
    ): SpeedDialInput {
        return {
            id: speedDialData?.id,
            shortNumber: speedDialData?.shortNumber ?? firstAvailableShortNumber,
            targetNumber: speedDialData?.targetNumber ?? "",
            description: speedDialData?.description ?? "",
            userId,
        };
    }
}

type DataSource = {
    loading: boolean;
    availableShortNumbers: string[];
};

function useDataSource(userId: number, speedDialId: number | undefined, isOpen: boolean): DataSource {
    const {data, loading} = useSpeedDialShortNumbersQuery({
        variables: {
            userId,
            speedDialId,
        },
        fetchPolicy: "no-cache",
        skip: !isOpen,
    });

    if (loading)
        return {
            loading: true,
            availableShortNumbers: undefined,
        };

    return {
        loading: false,
        availableShortNumbers: data.speedDialShortNumbers,
    };
}

type Props = {
    isOpen: boolean;
    close: () => void;
    onSuccess: () => void;
    userId: number;
    speedDialData?: Omit<SpeedDialInput, "userId">;
};

type SaveDialogContentProps = {
    dataSource: DataSource;
} & Omit<Props, "isOpen">;

const SaveDialogContent = ({dataSource, userId, speedDialData, close, onSuccess}: SaveDialogContentProps) => {
    const [submitting, setSubmitting] = useState(false);

    const form = new SaveDialogForm(userId, dataSource.availableShortNumbers[0], speedDialData);
    const errorHelper = new ErrorHelper(form.errors);

    const handleSubmit = async () => {
        if (submitting) return;
        setSubmitting(true);

        if (await form.submit()) {
            close();
            onSuccess();
        }

        setSubmitting(false);
    };

    return (
        <Dialog open={true}>
            <DialogTitle>
                {speedDialData?.id ? (
                    <FormattedMessage id="update_speed_dial_title" defaultMessage="Update speed dial" />
                ) : (
                    <FormattedMessage id="create_speed_dial_title" defaultMessage="Create speed dial" />
                )}
            </DialogTitle>
            <DialogContent>
                <FormContainer style={{margin: "1em 0"}}>
                    <FormattedFormRow
                        label={defineMessage({id: "short_number", defaultMessage: "Short number"})}
                        required
                    >
                        <Select
                            variant="standard"
                            value={form.values.shortNumber}
                            onChange={(event) => {
                                form.updateValues({shortNumber: event.target.value});
                            }}
                            renderValue={(value) => formatShortNumberForSpeedDial(value)}
                        >
                            {dataSource.availableShortNumbers.map((shortNumber) => (
                                <MenuItem key={shortNumber} value={shortNumber}>
                                    {formatShortNumberForSpeedDial(shortNumber)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({id: "target_number", defaultMessage: "Target number"})}
                        required
                    >
                        <TextField
                            variant="standard"
                            fullWidth
                            required
                            type="text"
                            margin="dense"
                            value={form.values.targetNumber}
                            error={errorHelper.hasError("targetNumber")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("targetNumber")} />}
                            onChange={(event) => form.updateValues({targetNumber: event.target.value})}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({id: "description", defaultMessage: "Description"})}
                        required
                    >
                        <TextField
                            variant="standard"
                            fullWidth
                            required
                            type="text"
                            margin="dense"
                            value={form.values.description}
                            error={errorHelper.hasError("description")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("description")} />}
                            onChange={(event) => form.updateValues({description: event.target.value})}
                        />
                    </FormattedFormRow>
                </FormContainer>
                <DialogActions>
                    <FormDialogActionButtons
                        mainAction={handleSubmit}
                        mainButtonEnabled={!submitting && form.hasChanged}
                        secondaryAction={() => form.reset()}
                        secondaryButtonEnabled={!submitting && form.hasChanged}
                        cancel={close}
                        cancelDisabled={submitting}
                        pending={submitting}
                    />
                </DialogActions>
            </DialogContent>
        </Dialog>
    );
};

const SaveDialog = ({isOpen, close, onSuccess, userId, speedDialData}: Props) => {
    const dataSource = useDataSource(userId, speedDialData?.id, isOpen);

    if (!isOpen) return null;
    if (dataSource.loading)
        return (
            <Dialog open={true}>
                <DialogTitle>
                    <LoadingIcon />
                </DialogTitle>
            </Dialog>
        );

    return (
        <SaveDialogContent
            dataSource={dataSource}
            userId={userId}
            speedDialData={speedDialData}
            close={close}
            onSuccess={onSuccess}
        />
    );
};

export default SaveDialog;
